import { AppSelect } from '../ui/AppSelect';
import React, { useState, useRef, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Wand2, ArrowLeft, Loader2, Copy, Save, RefreshCw, BookOpen, Lightbulb } from "lucide-react";
import { authenticatedFetch } from "../../utils/api";
import { API_HOST } from "../../utils/constants";
import { useTheme } from "../../context/ThemeContext";
import { useToast } from "../../context/ToastContext";
import { runTaskToCompletion } from "../../services/taskService";

const STYLES = [
  { id: "", label: "不限" },
  { id: "悬疑烧脑，反转不断", label: "悬疑" },
  { id: "甜宠轻喜剧，高甜撒糖", label: "甜宠" },
  { id: "都市现实主义，贴近生活", label: "都市" },
  { id: "古装权谋，宫斗暗涌", label: "古装" },
  { id: "科幻未来感，脑洞大开", label: "科幻" },
  { id: "恐怖惊悚，氛围感拉满", label: "恐怖" },
];

export default function AIWriterPage() {
  const { projectId, episodeId } = useParams();
  const navigate = useNavigate();
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();

  const [mode, setMode] = useState("concept"); // concept | outline
  const [concept, setConcept] = useState("");
  const [episodes, setEpisodes] = useState(1);
  const [wordsPerEpisode, setWordsPerEpisode] = useState(3000);
  const [style, setStyle] = useState("");
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState("");
  const [outline, setOutline] = useState(null);
  const [episodeList, setEpisodeList] = useState([]);
  const [selectedEpisodeId, setSelectedEpisodeId] = useState(episodeId || "");
  const resultRef = useRef(null);
  const abortRef = useRef(null);

  // 加载大纲和集数列表
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const data = await authenticatedFetch(`${API_HOST}/api/outline?projectId=${projectId}`);
        setOutline(data);
      } catch {}
    })();
    (async () => {
      try {
        const eps = await authenticatedFetch(`${API_HOST}/api/project/episode/list?projectId=${projectId}`);
        if (Array.isArray(eps)) setEpisodeList(eps);
      } catch {}
    })();
  }, [projectId]);

  // 提交到后端总闸任务队列，避免长连接断开导致结果丢失
  const handleGenerate = useCallback(async () => {
    if (mode === "concept" && !concept.trim()) { toast.warning("请输入故事概念"); return; }
    if (mode === "outline" && !selectedEpisodeId) { toast.warning("请选择集数"); return; }

    setGenerating(true);
    setResult("");
    abortRef.current?.abort?.();
    const controller = new AbortController();
    abortRef.current = controller;

    const provider = localStorage.getItem("ai_provider") || "omini";
    const taskType = mode === "concept" ? "SCRIPT_WRITER_FROM_CONCEPT" : "SCRIPT_WRITER_FROM_OUTLINE";
    const payload = mode === "concept"
      ? { concept, episodes, wordsPerEpisode, style: style || null }
      : { projectId: Number(projectId), episodeId: Number(selectedEpisodeId) };

    try {
      const completed = await runTaskToCompletion(taskType, payload, {
        provider,
        projectId: Number(projectId),
        episodeId: mode === "outline" ? Number(selectedEpisodeId) : undefined,
        source: mode === "concept" ? "script_writer_from_concept" : "script_writer_from_outline",
        signal: controller.signal,
        timeoutMs: 60 * 60 * 1000,
        onProgress: (status) => {
          if (status?.currentStageText) {
            setResult(status.currentStageText);
          }
        },
      });
      const value = completed?.result;
      if (typeof value === "string") {
        setResult(value);
      } else if (value?.description) {
        setResult(value.description);
      } else if (value?.text) {
        setResult(value.text);
      } else {
        setResult(JSON.stringify(value ?? "", null, 2));
      }
    } catch (err) {
      if (err?.name !== "AbortError") {
        toast.error("生成失败: " + err.message);
      }
    } finally {
      setGenerating(false);
      abortRef.current = null;
    }
  }, [mode, concept, episodes, wordsPerEpisode, style, projectId, selectedEpisodeId, toast]);

  // 保存到集数
  const handleSave = useCallback(async () => {
    if (!result.trim()) return;
    const targetEpisodeId = mode === "outline" ? selectedEpisodeId : null;

    if (targetEpisodeId) {
      try {
        await authenticatedFetch(`${API_HOST}/api/project/episode/update-script`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ episodeId: Number(targetEpisodeId), scriptContent: result }),
        });
        toast.success("已保存到集数剧本");
      } catch (e) {
        toast.error("保存失败: " + e.message);
      }
    } else {
      try {
        await authenticatedFetch(`${API_HOST}/api/script/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId: Number(projectId), title: "AI生成剧本", content: result }),
        });
        toast.success("已保存到剧本库");
      } catch (e) {
        toast.error("保存失败: " + e.message);
      }
    }
  }, [result, mode, selectedEpisodeId, projectId, toast]);

  const handleCopy = () => {
    navigator.clipboard.writeText(result);
    toast.success("已复制到剪贴板");
  };

  // 自动滚动到底部
  useEffect(() => {
    if (resultRef.current && generating) {
      resultRef.current.scrollTop = resultRef.current.scrollHeight;
    }
  }, [result, generating]);

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: "var(--color-bg-primary)" }}>
      {/* 顶栏 */}
      <div className="shrink-0 px-6 py-4 flex items-center gap-3" style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-lg transition-all hover:bg-[var(--color-bg-tertiary)]" style={{ color: "var(--color-text-muted)" }}>
          <ArrowLeft size={18} />
        </button>
        <Wand2 size={20} style={{ color: theme.colors.accentPrimary }} />
        <h1 className="text-lg font-bold" style={{ color: "var(--color-text-primary)" }}>AI 剧本创作</h1>
      </div>

      <div className="flex-1 overflow-y-auto px-6 py-5">
        <div className="max-w-3xl mx-auto space-y-5">

          {/* 模式切换 */}
          <div className="flex gap-2">
            <button onClick={() => setMode("concept")}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
              style={{
                backgroundColor: mode === "concept" ? `${theme.colors.accentPrimary}15` : "var(--color-bg-secondary)",
                color: mode === "concept" ? theme.colors.accentPrimary : "var(--color-text-secondary)",
                border: `1px solid ${mode === "concept" ? `${theme.colors.accentPrimary}40` : "var(--color-border-primary)"}`,
              }}>
              <Lightbulb size={16} /> 概念→剧本
            </button>
            <button onClick={() => setMode("outline")}
              className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all"
              style={{
                backgroundColor: mode === "outline" ? `${theme.colors.accentPrimary}15` : "var(--color-bg-secondary)",
                color: mode === "outline" ? theme.colors.accentPrimary : "var(--color-text-secondary)",
                border: `1px solid ${mode === "outline" ? `${theme.colors.accentPrimary}40` : "var(--color-border-primary)"}`,
              }}>
              <BookOpen size={16} /> 大纲→剧本
            </button>
          </div>

          {/* 概念模式 */}
          {mode === "concept" && (
            <div className="space-y-4 animate-in fade-in duration-200">
              <div>
                <label className="text-xs font-bold mb-1.5 block" style={{ color: "var(--color-text-secondary)" }}>故事概念</label>
                <textarea
                  value={concept}
                  onChange={(e) => setConcept(e.target.value)}
                  rows={4}
                  className="w-full rounded-xl px-4 py-3 text-sm outline-none resize-none leading-relaxed"
                  style={{ backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}
                  placeholder="描述你的故事概念，如：都市悬疑短剧，女主失忆后发现自己是凶手，每集一个反转..."
                />
              </div>
              <div className="flex gap-3">
                <div className="flex-1">
                  <label className="text-xs font-bold mb-1.5 block" style={{ color: "var(--color-text-secondary)" }}>集数</label>
                  <input type="number" min={1} max={30} value={episodes} onChange={(e) => setEpisodes(Number(e.target.value))}
                    className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                    style={{ backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-bold mb-1.5 block" style={{ color: "var(--color-text-secondary)" }}>每集字数</label>
                  <input type="number" min={500} max={10000} step={500} value={wordsPerEpisode} onChange={(e) => setWordsPerEpisode(Number(e.target.value))}
                    className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                    style={{ backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-bold mb-1.5 block" style={{ color: "var(--color-text-secondary)" }}>风格</label>
                  <AppSelect value={style} onChange={(e) => setStyle(e.target.value)}
                    className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                    style={{ backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}>
                    {STYLES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </AppSelect>
                </div>
              </div>
            </div>
          )}

          {/* 大纲模式 */}
          {mode === "outline" && (
            <div className="space-y-4 animate-in fade-in duration-200">
              {outline?.outlineJson ? (
                <div className="rounded-xl p-4" style={{ backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)" }}>
                  <div className="text-xs font-bold mb-2" style={{ color: "var(--color-text-secondary)" }}>项目大纲（已加载）</div>
                  <p className="text-xs leading-relaxed line-clamp-6" style={{ color: "var(--color-text-muted)" }}>
                    {outline.outlineJson.substring(0, 500)}...
                  </p>
                </div>
              ) : (
                <div className="rounded-xl p-4 text-center" style={{ backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)" }}>
                  <p className="text-sm" style={{ color: "var(--color-text-muted)" }}>该项目暂无大纲，请先在「剧情大纲」页面生成</p>
                </div>
              )}
              <div>
                <label className="text-xs font-bold mb-1.5 block" style={{ color: "var(--color-text-secondary)" }}>选择集数</label>
                <AppSelect value={selectedEpisodeId} onChange={(e) => setSelectedEpisodeId(e.target.value)}
                  className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                  style={{ backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)", color: "var(--color-text-primary)" }}>
                  <option value="">请选择...</option>
                  {episodeList.map(ep => (
                    <option key={ep.id} value={ep.id}>第{ep.sortOrder}集: {ep.title}</option>
                  ))}
                </AppSelect>
              </div>
            </div>
          )}

          {/* 生成按钮 */}
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="w-full py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all"
            style={{
              backgroundColor: generating ? "var(--color-bg-tertiary)" : theme.colors.accentPrimary,
              color: generating ? "var(--color-text-muted)" : "white",
              cursor: generating ? "not-allowed" : "pointer",
            }}
          >
            {generating ? <Loader2 size={16} className="animate-spin" /> : <Wand2 size={16} />}
            {generating ? "生成中..." : "开始生成"}
          </button>

          {/* 结果区 */}
          {(result || generating) && (
            <div className="rounded-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300" style={{ backgroundColor: "var(--color-bg-secondary)", border: "1px solid var(--color-border-primary)" }}>
              <div className="px-4 py-2.5 flex items-center justify-between" style={{ borderBottom: "1px solid var(--color-border-primary)" }}>
                <span className="text-xs font-bold" style={{ color: "var(--color-text-secondary)" }}>
                  {generating ? "生成中..." : `生成完成 · ${result.length} 字`}
                </span>
                {!generating && result && (
                  <div className="flex items-center gap-1.5">
                    <button onClick={handleCopy} className="flex items-center gap-1 px-2 py-1 rounded-lg text-2xs transition-all hover:bg-[var(--color-bg-tertiary)]" style={{ color: "var(--color-text-muted)" }}>
                      <Copy size={12} /> 复制
                    </button>
                    <button onClick={handleSave} className="flex items-center gap-1 px-2 py-1 rounded-lg text-2xs transition-all" style={{ backgroundColor: `${theme.colors.accentPrimary}15`, color: theme.colors.accentPrimary }}>
                      <Save size={12} /> 保存
                    </button>
                    <button onClick={handleGenerate} className="flex items-center gap-1 px-2 py-1 rounded-lg text-2xs transition-all hover:bg-[var(--color-bg-tertiary)]" style={{ color: "var(--color-text-muted)" }}>
                      <RefreshCw size={12} /> 重新生成
                    </button>
                  </div>
                )}
              </div>
              <div ref={resultRef} className="px-5 py-4 max-h-[60vh] overflow-y-auto custom-scrollbar">
                <pre className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "var(--color-text-primary)", fontFamily: "inherit" }}>
                  {result}
                  {generating && <span className="inline-block w-1.5 h-4 ml-0.5 animate-pulse rounded-sm" style={{ backgroundColor: theme.colors.accentPrimary, verticalAlign: "text-bottom" }} />}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
